import * as React from "react";
import {createContext, useCallback, useContext, useRef, useState} from "react";

import {AdminApi} from "../../../api/api";
import {useAdminApi} from "../../../api/api-context";
import {TagSearchInput} from "../../../api/values/tag-search-input";

export interface TagItem {
    label: string;
    slug: string;
}

export interface TagCacheContextValue {
    tags?: TagItem[];
    load(mediaId: string): void;
}

async function loadTags(
    adminApi: AdminApi,
    mediaId: string,
): Promise<{
    tags: TagItem[];
}> {
    const tags: TagItem[] = [];
    const output = await adminApi.getTags(new TagSearchInput({mediaId}));

    output.tags.forEach((item) => {
        tags.push({label: item.label, slug: item.slug});
    });

    return {tags: tags};
}

export const TagCacheContext = createContext<TagCacheContextValue | undefined>(undefined);

type LoadingState = "initial" | "loading" | "loaded";

type TagCacheProviderProps = React.PropsWithChildren<{}>;

export const TagCacheProvider: React.FC<TagCacheProviderProps> = ({children}) => {
    const [data, setData] = useState<{tags: TagItem[]} | undefined>(undefined);
    const adminApi = useAdminApi();

    const loadingStateRef = useRef<LoadingState>("initial");

    const load = useCallback(
        async (mediaId: string) => {
            if (loadingStateRef.current !== "initial") {
                return;
            }
            loadingStateRef.current = "loading";

            try {
                const result = await loadTags(adminApi, mediaId);
                setData(result);
                loadingStateRef.current = "loaded";
            } catch (e) {
                console.error(e);
                loadingStateRef.current = "initial";
            }
        },
        [adminApi],
    );

    return (
        <TagCacheContext.Provider
            value={{
                ...data,
                load,
            }}
        >
            {children}
        </TagCacheContext.Provider>
    );
};

export function useTagCache() {
    const value = useContext(TagCacheContext);
    if (!value) {
        throw new Error("TagCacheContextProvider is not provided");
    }
    return value;
}
